import React, {memo} from 'react';
import {
   FormControl,
   InputLabel,
   Select,
   SelectProps,
   MenuItem,
   FormHelperText,
   OutlinedInput,
   ListItemText,
   Typography,
   Divider,
} from '@mui/material';
import {DEFAULT_NULL_VALUE} from 'common/constants';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import {isObject} from 'lodash';

export type DividerOption = {divider: boolean};
export type DropdownObjectOption = {
   id: string | number;
   name: string;
   labelInfo?: string;
};
export const isDropdownObjectOption = (option: any): option is DropdownObjectOption =>
   option.name && (typeof option.id === 'string' || typeof option.id === 'number');
export type DropdownOption = DropdownObjectOption | DividerOption | string | number;
export type DropdownOptions = DropdownOption[];

const renderItems = (options: DropdownOptions) => {
   if (!options) {
      return null;
   }
   return options.map((option) => {
      const isObj = isObject(option); // isDropdownObjectOption(option);
      const opt = option as DropdownObjectOption;
      const value = isObj ? opt.id : option;
      const name = isObj ? opt.name : option;
      const labelInfo = isObj ? opt.labelInfo : null;

      return (option as DividerOption)?.divider ? (
         <Divider />
      ) : (
         <MenuItem key={value} value={value}>
            {/* <ListItemIcon>
               <ContentCut fontSize="small" />
            </ListItemIcon> */}
            <ListItemText> {name}</ListItemText>
            {labelInfo && (
               <Typography variant="body2" sx={{color: 'text.secondary'}}>
                  {labelInfo}
               </Typography>
            )}
         </MenuItem>
      );
   });
};

export type DropdownProps = SelectProps & {
   includeEmptyValue?: boolean;
   options: DropdownOptions;
   error?: any;
};
const Dropdown = memo(
   ({
      label,
      options,
      name,
      includeEmptyValue = false,
      variant = 'outlined',
      required,
      className,
      error,
      value,
      disabled,
      fullWidth = true,
      ...otherProps
   }: DropdownProps) => {
      const readOnly = React.useContext(readOnlyContext);
      const labelId = `${name}_id`;

      return (
         <FormControl
            fullWidth={fullWidth}
            variant={variant}
            required={required}
            error={!!error}
            className={className}
            disabled={disabled || readOnly}
         >
            <InputLabel shrink={true} id={labelId}>
               {label}
            </InputLabel>
            <Select
               labelId={labelId}
               label={label}
               name={name}
               input={<OutlinedInput label={label} notched />}
               {...otherProps}
               value={value ?? ''}
            >
               {includeEmptyValue ? <MenuItem value={''}>{DEFAULT_NULL_VALUE}</MenuItem> : null}
               {renderItems(options)}
            </Select>
            {error ? <FormHelperText>{error}</FormHelperText> : null}
         </FormControl>
      );
   }
);

export default Dropdown;
